import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { validateVoucherCode } from "../../features/booking/bookingApi";
import { selectBooking, setBookingVoucherCode } from "../../features/booking/bookingSlice";
import { selectHotel } from "../../features/hotel/hotelSlice";
import { fetchHotelRoomAvailability } from "../../features/hotel/hotelApi";
import { normalizeRoomCategoryAvailabilities } from "../../helpers/mewsPrice";
import { voucherAppliesToRooms } from "../../helpers/roomCategory";
import { createRef, useEffect, useRef, useState } from "react";
import Refresh from "../Svg/Refresh";
import Check from "../Svg/Check";
import ArrowLeft from "../Svg/ArrowLeft";
import { useTranslation } from "react-i18next";

interface IInputField {
  type: string;
  label: string;
  name: string;
  value: string | null;
  error: boolean | null;
  validate: { [key: string]: string | number | boolean };
  errorMessages: { [key: string]: string };
  valueCallback: (obj: { [key: string]: string }) => void;
  validationCallBack: (obj: { [key: string]: boolean | null }) => void;
}

function VoucherField(props: IInputField) {
  const { type, label, name, value, error, valueCallback } = props;

  const dispatch = useAppDispatch();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const [active, setActive] = useState<boolean>(false);
  const inputRef = createRef<HTMLInputElement>();
  const isInitialMount = useRef(true);
  const [voucherState, setVoucherState] = useState<string>("");
  const {t} = useTranslation();
  

  useEffect(() => {
    if (value !== "") {
      setActive(true);
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (value !== "") {
        handleBlur();
      }
    } else {
      handleBlur();

      const delayDebounceFn = setTimeout(() => {
        if (value !== "" && value !== null) {
          validateVoucher();
        }
      }, 1500);

      return () => clearTimeout(delayDebounceFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, error]);

  const handleFocus = () => {
    setActive(true);
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    setVoucherState("");
  };

  const handleInput = (
    e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value: string = e.currentTarget.value.trim();
    let obj: { [key: string]: string } = {};
    obj[e.currentTarget.name as keyof typeof obj] = value;
    valueCallback(obj);
  };

  const validateVoucher = async () => {
    setVoucherState("loading");
    try {
      const response = await validateVoucherCode(hotel, value);
      if (!response.data.IsValid) {
        setVoucherState("inValid");
        return;
      }

      if (booking.StartUtc && booking.EndUtc && booking.Rooms.length) {
        const availability = await fetchHotelRoomAvailability(
          hotel,
          booking.StartUtc,
          booking.EndUtc,
          value,
        );
        const currencyCode = hotel.Culture.CurrencyCode;
        const applies = voucherAppliesToRooms(
          booking.Rooms,
          availability.data.Rates ?? [],
          normalizeRoomCategoryAvailabilities(
            availability.data.RoomCategoryAvailabilities,
            currencyCode,
          ),
          currencyCode,
        );
        if (!applies) {
          setVoucherState("notApplicable");
          return;
        }
      }

      setVoucherState("valid");
      dispatch(setBookingVoucherCode(value));
    } catch {
      setVoucherState("inValid");
    }
  };

  return (
    <div onClick={() => handleFocus()}>
      <span>{label}</span>
      <div
        className={`flex items-center gap-2 rounded-md p-2 border mt-1 ${
          active ? "c-form__element--active" : ""
        } ${voucherState === "inValid" || voucherState === "notApplicable" ? "border-red-400 bg-red-200 text-red-600" : ' bg-white' + (
          voucherState === "valid" ? " border-green-600" : " border-custom-400"
        )}`}
      >
        <div className="flex-grow w-full">
          <input
            ref={inputRef}
            name={name}
            type={type}
            value={value ?? ""}
            onChange={handleInput}
            onFocus={() => handleFocus()}
            onBlur={() => handleBlur()}
            className="w-full border-transparent focus:border-transparent focus:ring-0 p-0 outline-none text-input"
          />
        </div>

        <div
          className={`cursor-pointer ${active && voucherState !== "valid" ? "" : "hidden"}`}
          onClick={() => validateVoucher()}
        >
          <Refresh className={`w-6 h-6 ${voucherState === "loading" ? "animate-spin" : "hidden"}`} />
          <ArrowLeft className={`rotate-180 w-6 h-6 ${voucherState === "loading" ? "hidden" : ""}`} />
        </div>

        <div className={`text-green-600 ${active && voucherState === "valid" ? "" : "hidden"}`}>
          <Check />
        </div>
      </div>

      {voucherState === "inValid" && (
        <div className="text-fluid-xs text-red-600 text-right mt-1">{t("scenes.summary.voucherInvalid")}</div>
      )}
      {voucherState === "notApplicable" && (
        <div className="text-fluid-xs text-red-600 text-right mt-1">{t("scenes.summary.voucherNotApplicable")}</div>
      )}
    </div>
  );
}

export default VoucherField;
